import { lazy } from "react";

const ResourcesDetail = lazy(() => import("./components/resources/ResourcesDetail"));
const ZoomTest = lazy(() => import("./components/zoom/ZoomTest"));
const Announcement = lazy(() => import("./pages/Announcement"));
const AssignmentInbox = lazy(() => import("./pages/AssignmentInbox"));
// Legacy Assignment.js (batch-card grid) kept on disk but no longer
// routed — replaced by AssignmentInbox below.
const AssignmentForm = lazy(() => import("./pages/AssignmentForm"));
const AssignmentView = lazy(() => import("./pages/AssignmentView"));
// Legacy Batch.js/BatchList.js/BatchHeader.js/BatchDetailModal.js kept on
// disk but no longer routed — replaced by the redesigned Cohorts
// workspace below.
const CohortWorkspace = lazy(() => import("./pages/CohortWorkspace"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
// Legacy Email.js/EmailChatDetail.js kept on disk but no longer routed —
// replaced by the redesigned Messages workspace below.
const Messages = lazy(() => import("./pages/Messages"));
const Home = lazy(() => import("./pages/Home"));
const Login = lazy(() => import("./pages/Login"));
const SsoLanding = lazy(() => import("./pages/SsoLanding"));
const MockTest = lazy(() => import("./pages/MockTest"));
const Module = lazy(() => import("./pages/Module"));
const ModuleResources = lazy(() => import("./pages/ModuleResources"));
const ModuleVideo = lazy(() => import("./pages/ModuleVideo"));
const ModuleView = lazy(() => import("./pages/ModuleView"));
const QnaInbox = lazy(() => import("./pages/QnaInbox"));
// Legacy QnaList.js/QnaAccordian.js — still routed (at /qna-legacy*, not
// /qna) rather than retired: every real Q&A entry that existed before this
// redesign lives only in the old moduleModal.qna[] store (no lessonId on
// those rows, so migrating them into the new lms-lesson-qna model would be
// lossy/guessed — see Scope Decision 1). The new /qna page reads only the
// new model, so this is the only place that historical data is visible or
// answerable.
const QnaList = lazy(() => import("./pages/QnaList"));
const Resources = lazy(() => import("./pages/Resources"));
const ResourceUpload = lazy(() => import("./pages/ResourceUpload"));
const ResourceActivity = lazy(() => import("./pages/ResourceActivity"));
const ResourceShared = lazy(() => import("./pages/ResourceShared"));
const StudentReport = lazy(() => import("./pages/StudentReport"));
const VideoView = lazy(() => import("./pages/VideoView"));

const HomeComponent = () => {
  if (localStorage.getItem("oeson_auth")) {
    return <Home />;
  } else {
    return <Login />;
  }
};

const RoutesArr = [
  {
    name: "Home",
    path: "/",
    key: "Home",
    route: "/",
    page: <HomeComponent />,
    private: true,
  },
  {
    name: "Batch",
    path: "/batch",
    key: "Batch",
    route: "/batch",
    page: <CohortWorkspace />,
    private: true,
  },
  {
    name: "BatchActive",
    path: "/batch-active",
    key: "BatchActive",
    route: "/batch-active",
    page: <CohortWorkspace />,
    private: true,
  },
  {
    name: "BatchInactive",
    path: "/batch-inactive",
    key: "BatchInactive",
    route: "/batch-inactive",
    page: <CohortWorkspace />,
    private: true,
  },
  {
    name: "Module",
    path: "/batch/module/:id",
    key: "Module",
    route: "/batch/module/:id",
    page: <Module />,
    private: true,
  },
  {
    name: "ModuleStudents",
    path: "/batch/students/:id",
    key: "ModuleStudents",
    route: "/batch/students/:id",
    page: <Module />,
    private: true,
  },
  {
    name: "BatchReport",
    path: "/batch/report/:id",
    key: "BatchReport",
    route: "/batch/report/:id",
    page: <Module />,
    private: true,
  },
  {
    name: "BatchProgress",
    path: "/batch/progress/:id",
    key: "BatchProgress",
    route: "/batch/progress/:id",
    page: <Module />,
    private: true,
  },
  {
    name: "BatchAttendance",
    path: "/batch/attendance/:id",
    key: "BatchAttendance",
    route: "/batch/attendance/:id",
    page: <Module />,
    private: true,
  },
  {
    name: "BatchAnnouncements",
    path: "/batch/announcements/:id",
    key: "BatchAnnouncements",
    route: "/batch/announcements/:id",
    page: <Module />,
    private: true,
  },
  {
    name: "BatchDiscussions",
    path: "/batch/discussions/:id",
    key: "BatchDiscussions",
    route: "/batch/discussions/:id",
    page: <Module />,
    private: true,
  },
  {
    name: "BatchResources",
    path: "/batch/resources/:id",
    key: "BatchResources",
    route: "/batch/resources/:id",
    page: <Module />,
    private: true,
  },
  {
    name: "BatchActivity",
    path: "/batch/activity/:id",
    key: "BatchActivity",
    route: "/batch/activity/:id",
    page: <Module />,
    private: true,
  },
  {
    name: "ModuleStudentsActive",
    path: "/batch/students-active/:id",
    key: "ModuleStudentsActive",
    route: "/batch/students-active/:id",
    page: <Module />,
    private: true,
  },
  {
    name: "ModuleStudentsDeActive",
    path: "/batch/students-deactive/:id",
    key: "ModuleStudentsDeActive",
    route: "/batch/students-deactive/:id",
    page: <Module />,
    private: true,
  },
  {
    name: "ModuleSchedule",
    path: "/batch/schedule/:id",
    key: "ModuleSchedule",
    route: "/batch/schedule/:id",
    page: <Module />,
    private: true,
  },
  {
    name: "ModuleView",
    path: "/batch/module/view/:id",
    key: "ModuleView",
    route: "/batch/module/view/:id",
    page: <ModuleView />,
    private: true,
  },
  {
    name: "ModuleVideo",
    path: "/batch/module/video/:id",
    key: "ModuleVideo",
    route: "/batch/module/video/:id",
    page: <ModuleVideo />,
    private: true,
  },
  {
    name: "VideoView",
    path: "/batch/module/video/view/:id/:urlId",
    key: "VideoView",
    route: "/batch/module/video/view/:id/:urlId",
    page: <VideoView />,
    private: true,
  },
  {
    name: "StudyMaterial",
    path: "/batch/module/study-material/:id",
    key: "StudyMaterial",
    route: "/batch/module/study-material/:id",
    page: <ModuleResources />,
    private: true,
  },
  {
    name: "Work",
    path: "/work",
    key: "Work",
    route: "/work",
    page: <AssignmentInbox />,
    private: true,
  },
  {
    name: "workView",
    path: "/work/view/:id",
    key: "workView",
    route: "/work/view/:id",
    page: <AssignmentView />,
    private: true,
  },
  {
    name: "workForm",
    path: "/work/form",
    key: "workForm",
    route: "/work/form",
    page: <AssignmentForm />,
    private: true,
  },
  {
    name: "workFormID",
    path: "/work/form/:id",
    key: "workFormID",
    route: "/work/form/:id",
    page: <AssignmentForm />,
    private: true,
  },
  {
    name: "Profile",
    path: "/profile",
    key: "Profile",
    route: "/profile",
    page: <EditProfile />,
    private: true,
  },
  {
    name: "QnA",
    path: "/qna",
    key: "QnA",
    route: "/qna",
    page: <QnaInbox />,
    private: true,
  },
  {
    name: "QnaLegacy",
    path: "/qna-legacy",
    key: "QnaLegacy",
    route: "/qna-legacy",
    page: <QnaList />,
    private: true,
  },
  {
    name: "QnaLegacyAnswered",
    path: "/qna-legacy-answered",
    key: "QnaLegacy",
    route: "/qna-legacy-answered",
    page: <QnaList />,
    private: true,
  },
  {
    name: "QnaLegacyUnanswered",
    path: "/qna-legacy-unanswered",
    key: "QnaLegacy",
    route: "/qna-legacy-unanswered",
    page: <QnaList />,
    private: true,
  },
  {
    name: "Login",
    path: "/login",
    key: "Login",
    route: "/login",
    page: <Login />,
  },
  {
    name: "MentorSSO",
    path: "/sso",
    key: "MentorSSO",
    route: "/sso",
    page: <SsoLanding />,
  },
  {
    name: "Zoom Test",
    path: "/zoom-test",
    key: "Zoom Test",
    route: "/zoom-test",
    page: <ZoomTest />,
    private: true,
  },
  {
    name: "Announcement",
    path: "/announcement",
    key: "Announcement",
    route: "/announcement",
    page: <Announcement />,
    private: true,
  },
  {
    name: "student-report",
    path: "/student-report",
    key: "student-report",
    route: "/student-report",
    page: <StudentReport />,
    private: true,
  },
  {
    name: "mock-tests",
    path: "/mock-tests",
    key: "mock-tests",
    route: "/mock-tests",
    page: <MockTest />,
    private: true,
  },
  {
    name: "resources",
    path: "/resources",
    key: "resources",
    route: "/resources",
    page: <Resources />,
    private: true,
  },
  {
    name: "resources-upload",
    path: "/resources/upload",
    key: "resources-upload",
    route: "/resources/upload",
    page: <ResourceUpload />,
    private: true,
  },
  {
    name: "resources-activity",
    path: "/resources/activity",
    key: "resources-activity",
    route: "/resources/activity",
    page: <ResourceActivity />,
    private: true,
  },
  {
    name: "resources-shared",
    path: "/resources/shared",
    key: "resources-shared",
    route: "/resources/shared",
    page: <ResourceShared />,
    private: true,
  },
  {
    name: "resources-detail",
    path: "/resources/:id",
    key: "resources-detail",
    route: "/resources/:id",
    page: <ResourcesDetail />,
    private: true,
  },
  {
    name: "email",
    path: "email",
    key: "email",
    route: "email",
    page: <Messages />,
    private:true
  },
];

export default RoutesArr;
